import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  Button,
  Box,
  Tooltip,
  IconButton,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ExpertListItemDto } from '../../types/expert';
import ChatIcon from '@mui/icons-material/Chat';
import { UserAvatar } from '../common/UserAvatar.tsx';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import StarIcon from '@mui/icons-material/Star';

interface ExpertCardProps {
  expert: ExpertListItemDto;
  onRequestConsultation: (expert: ExpertListItemDto) => void;
  onStartChat: (expert: ExpertListItemDto) => void;
  onRequestConnection?: (expert: ExpertListItemDto) => void;
  showChatButton?: boolean;
}

export const ExpertCard: React.FC<ExpertCardProps> = ({ expert, onRequestConsultation, onStartChat, onRequestConnection, showChatButton = true }) => {
  const navigate = useNavigate();
  const { interactionStatus } = expert;

  const getStatusChip = () => {
    if (interactionStatus === 'active') {
      return (
        <Tooltip title="Ви вже співпрацюєте з цим експертом">
          <Chip
            icon={<CheckCircleIcon />}
            label="Ваш експерт"
            size="small"
            color="success"
            variant="outlined"
            sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, bgcolor: 'background.paper' }}
          />
        </Tooltip>
      );
    }
    if (interactionStatus === 'pending') {
      return (
        <Tooltip title="Ваш запит очікує на підтвердження">
          <Chip
            icon={<HourglassEmptyIcon />}
            label="В очікуванні"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, bgcolor: 'background.paper' }}
          />
        </Tooltip>
      );
    }
    if (interactionStatus === 'rejected') {
      return (
        <Tooltip title="Заявку відхилено експертом">
          <Chip
            icon={<BlockIcon />}
            label="Відхилено"
            size="small"
            color="error"
            variant="outlined"
            sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, bgcolor: 'background.paper' }}
          />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <Card sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
        }
    }}>
      {getStatusChip()}
      <CardContent
        onClick={() => navigate(`/experts/${expert.id}`)}
        sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'pointer' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <UserAvatar 
            path={expert.avatar} 
            name={expert.name} 
            sx={{ width: 80, height: 80, mb: 2 }}
          />
          <Box>
            <Typography variant="h6" component="div" fontWeight="bold">
              {expert.name}
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
              {expert.specializationsString || 'Спеціалізація не вказана'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {expert.rating != null && (
                <Chip
                  icon={<StarIcon sx={{ color: '#f5a623 !important' }} />}
                  label={Number(expert.rating).toFixed(2)}
                  size="small"
                  sx={{ bgcolor: '#fff8e1', color: '#b26a00', fontWeight: 600, border: '1px solid #f5a623' }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', alignItems: 'center', p: '8px 16px', borderTop: '1px solid #f0f0f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {(interactionStatus === 'active' || interactionStatus === 'pending') ? (
            <>
              <Button size="medium" variant="contained" onClick={(e) => { e.stopPropagation(); onRequestConsultation(expert); }} sx={{ mr: 1 }}>
                Нова зустріч
              </Button>
              {showChatButton && (
                <Tooltip title="Написати в чат">
                  <IconButton onClick={(e) => { e.stopPropagation(); onStartChat(expert); }} color="primary">
                    <ChatIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : interactionStatus === 'rejected' ? (
            onRequestConnection ? (
              <Button size="medium" variant="outlined" color="primary" onClick={(e) => { e.stopPropagation(); onRequestConnection(expert); }}>
                Повторна заявка
              </Button>
            ) : (
              <Button size="medium" variant="outlined" disabled>
                Відхилено
              </Button>
            )
          ) : onRequestConnection ? (
            <Button size="medium" variant="outlined" color="primary" onClick={(e) => { e.stopPropagation(); onRequestConnection(expert); }}>
              Заявка на співпрацю
            </Button>
          ) : (
            <Button size="medium" variant="contained" onClick={(e) => { e.stopPropagation(); onRequestConsultation(expert); }}>
              Записатися
            </Button>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};